import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message, Input, Checkbox } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../../../utils/axiosInstance';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { agreementStatus } from '../../../utils/Constant';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useTheme } from '../../../utils/themeToggle/theme';
import SelectTemplateModal from '../../../components/modal/SelectTemplateModal';
import ColapseButton from '../../../components/button/Colapse';
import EmailThreadItems from './EmailThreadItems';
import ButtonComp from '../../../components/button/ButtonComp';
import GoogleButton from '../../../components/button/GoogleButton';

export default function EmailThread({ contract, fetchContracts }) {
    const [isReplyOpen, setIsReplyOpen] = useState(false);
    const [editorContent, setEditorContent] = useState('');
    const [quotedHistory, setQuotedHistory] = useState("");
    const [showQuotedHistory, setShowQuotedHistory] = useState(false);
    const [files, setFiles] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [ccRecipients, setCcRecipients] = useState([]);
    const [subject, setSubject] = useState('');
    const [status, setStatus] = useState('');
    const [orgEmails, setOrgEmails] = useState([]);
    const [messages, setMessages] = useState([]);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyAllMode, setReplyAllMode] = useState(false);
    const [attachDefaultAgreement, setAttachDefaultAgreement] = useState(true);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [sending, setSending] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    // const [canExecute, setCanExecute] = useState(null);

    const comp_id = useSelector((store) => store.auth.userInfo.company_id);
    const selfEmail = useSelector((store) => store.auth.userInfo.user_email);
    const userRole = useSelector((store) => store.auth.userInfo.role);
    const selfName = useSelector((store) => store.auth.userInfo.name);
    const google_auth = useSelector((store) => store.auth.userInfo.is_google_authenticated);
    const navigate = useNavigate();
    const { pk } = useParams();
    const quillRef = useRef(null);
    const { reactSelectTheme } = useTheme();
    // const company_id = Number(localStorage.getItem('companyId')) || comp_id;

    const modules = useMemo(
        () => ({
            toolbar: [
                [{ header: [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['link', 'image', 'video'],
                ['clean'],
            ],
        }),
        []
    );

    const fetchThreadDetails = async () => {
        try {
            const response = await axiosInstance.get(`/agreements/${pk}/conversation/`);
            setMessages(response.data.messages || []);
        } catch (error) {
            console.error('Error fetching thread details:', error);
        }
    };

    useEffect(() => {
        fetchThreadDetails();
    }, []);

    const loadOrgEmails = async () => {
        try {
            const companyId = localStorage.getItem("companyId") || comp_id;
            const res = await axiosInstance.post("/user/get_user_email/", {
                company_id: companyId,
                is_active: true,
            });
            console.log("Loaded org emails:", res?.data?.data);
            const users = res?.data?.data || [];
            setOrgEmails(users.map(u => u.email).filter(Boolean));
        } catch (e) {
            console.error("Error loading org emails:", e);
            setOrgEmails([]);
        }
    };

    const allRecipientsInOrg = useMemo(() => {
        if (!recipients?.length) return null; // 👈 neutral
        return recipients.every((r) => orgEmails.includes(r));
    }, [recipients, orgEmails]);


    const openCompose = async () => {
        setIsReplyOpen(true);
        setReplyingTo(null);
        setReplyAllMode(false);
        setAttachDefaultAgreement(false);
        setEditorContent('');
        setRecipients([]);
        setCcRecipients([]);
        setSubject('');
        setStatus('');
        setQuotedHistory('');
        setShowQuotedHistory(false);
        if (!orgEmails.length) {
            await loadOrgEmails();
        }
    };

    function getCleanEmailBody(html) {
        if (!html) return "";
        let cleaned = html;
        cleaned = cleaned.replace(/<blockquote[\s\S]*?<\/blockquote>/gi, "");
        cleaned = cleaned.replace(/<div[^>]+border-left[^>]*>[\s\S]*?<\/div>/gi, "");
        cleaned = cleaned.replace(/<div class="?gmail_quote"?.*?<\/div>/gis, "");
        cleaned = cleaned.replace(/On\s.+wrote:/gi, "");
        cleaned = cleaned.replace(/----+ Original Message ----+/gi, "");
        cleaned = cleaned.replace(/________________________________/gi, "");
        cleaned = cleaned.replace(/From:.+Sent:.+To:.+/gis, "");
        cleaned = cleaned.replace(/(^|\n)\s*&gt;.*$/gim, "");
        cleaned = cleaned.replace(/(^|\n)\s*>.*$/gim, "");
        cleaned = cleaned.replace(/Regards,([\s\S]*?)Contracts Team([\s\S]*)$/i, "Regards,<br/>Contracts Team");
        cleaned = cleaned.replace(/<div>\s*<\/div>/gi, "");
        cleaned = cleaned.replace(/(\r\n|\n|\r){2,}/g, "<br/>");
        cleaned = cleaned.replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>");
        cleaned = cleaned.replace(/^\s+|\s+$/g, "").replace(/^(<br\s*\/?>)+/gi, "").replace(/(<br\s*\/?>)+$/gi, "").replace(/(&nbsp;|\s)+$/gi, "");
        return cleaned.trim();
    }

    function formatQuotedBlock(msg) {
        const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : "";
        const sender = msg.sender_email || "Unknown";
        return `
            <div style="margin:8px 0;padding-left:8px;border-left:3px solid #e6e6e6;">
                <div style="font-size:12px;color:#444;margin-bottom:6px;">
                    <strong>${sender}</strong> <span style="color:#888;font-size:11px;margin-left:8px;">${time}</span>
                </div>
                <div style="font-size:13px;color:#222;">
                    ${msg.body || ""}
                </div>
            </div>
        `;
    }

    const buildQuotedHistoryUpTo = (clickedMsgId) => {
        if (!Array.isArray(messages) || messages.length === 0) return "";
        const idx = messages.findIndex(m => (m.id === clickedMsgId) || (String(m.id) === String(clickedMsgId)));
        if (idx === -1) {
            const found = messages.find(m => (m.id === clickedMsgId) || (String(m.id) === String(clickedMsgId)));
            return found ? formatQuotedBlock(found) : "";
        }
        const slice = messages.slice(0, idx + 1);
        const ordered = slice.slice().reverse();
        const blocks = ordered.map(m => formatQuotedBlock(m));
        return blocks.join('<div style="height:8px;"></div>');
    };

    const handleReplyClick = async (thread, mode) => {
        console.log("Reply clicked for thread:", thread, "Mode:", mode);
        setReplyingTo(thread);
        setIsReplyOpen(true);
        setShowQuotedHistory(false);
        if (!orgEmails.length) await loadOrgEmails();

        if (mode === "reply") {
            setRecipients([thread.sender_email].filter(e => e && e !== selfEmail));
            setSelectedUserIds([thread.sender]);
            setCcRecipients([]);
            setReplyAllMode(false);
        } else if (mode === "replyAll") {
            const allTo = [thread.sender_email, ...(thread.receiver_emails?.to || [])]
                .filter(e => e && e !== selfEmail);
            const allCc = (thread.receiver_emails?.cc || []).filter(e => e && e !== selfEmail);
            setRecipients([...new Set(allTo)]);
            setCcRecipients([...new Set(allCc)]);
            setReplyAllMode(true);
        }

        setSubject(`Re: ${thread.subject || contract?.subject || ""}`);
        setStatus("");
        setAttachDefaultAgreement(true);
        // Editor must be blank for user to type fresh
        setEditorContent("<p><br/></p>");

        // Build quoted history = ALL messages up to the clicked message (so 'pehle k sare msg')
        const built = buildQuotedHistoryUpTo(thread.id);
        setQuotedHistory(built || "");
        // Show quoted history by default (user asked it should appear)
        setShowQuotedHistory(false);
        if (!orgEmails.length) loadOrgEmails();
    };

    // 👇 Add this helper right above handleReplyClick
    const mapEmailsToUserIds = (emails = []) => {
        return emails
            .map(email => {
                const user = (contract?.contract_access_users || []).find(u => u.email === email);
                return user?.id || null;
            })
            .filter(Boolean);
    };



    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
    };

    const handleAttachmentSave = async () => {
        try {
            const formData = new FormData();
            formData.append("agreement_id", contract?.id);
            formData.append("company_id", localStorage.getItem("companyId") || comp_id);
            files.forEach((file) => {
                formData.append("attachment", file); // plural keep karna better hai
            });
            await axiosInstance.post(`/contract/attachment/`, formData);
            message.success("Document(s) Saved Successfully.");
            setFiles([]);
            fetchContracts();
        } catch (error) {
            message.error(error?.response?.data?.error || "Upload failed");
        }
    };

    const handleSend = async () => {
        if (sending) return;
        setSending(true);
        try {
            if (!editorContent || !recipients?.length) {
                message.error("Please enter content and recipients.");
                setSending(false);
                return;
            }

            if (allRecipientsInOrg && status?.trim()) {
                const formData = new FormData();
                formData.append("agreement_id", Number(pk));
                formData.append("body", editorContent);
                formData.append("subject", subject);
                formData.append("status", status);
                formData.append("receiver_emails", recipients.join(","));
                formData.append("cc_recipients", ccRecipients.join(","));
                formData.append("agreement_file_edit_permission_user_id", selectedUserIds.join(","));

                const hideLoading = message.loading('Sending contract...', 0);
                const res = await axiosInstance.post(`/contract/send_agreement/`, formData);
                hideLoading();

                if (contract.status === "FRESH_REQUEST") {
                    navigate(`/admin/contracts`);
                    return;
                }
                if (contract.status === "EXCUTED") {
                    console.log("in ecuted part ");

                }

                if (files.length > 0) {
                    await handleAttachmentSave();
                }
                await fetchThreadDetails();
                await fetchContracts();
                message.success('Contract sent successfully!');
                resetEditor();
                setSending(false);
                return;
            }

            // --- REPLY / REPLY ALL ---
            if (replyingTo) {
                const payload = new FormData();
                payload.append("agreement_id", Number(pk));
                payload.append("reply_to_message_id", replyingTo?.id || "");
                payload.append("body", editorContent);
                payload.append("quoted_history", quotedHistory || "");
                payload.append("include_history", quotedHistory ? "true" : "false");
                payload.append("reply_all", replyAllMode ? "true" : "false");
                payload.append("to", [...recipients, ...ccRecipients].join(","));
                payload.append("default_agreement_file", attachDefaultAgreement ? "true" : "false");

                files.forEach((file, idx) => {
                    payload.append("attachment", file, file.name || `local_${idx + 1}`);
                });
                const hide = message.loading('Sending reply...', 0);
                const res = await axiosInstance.post(`/agreement/emails/reply/`, payload);
                hide();
                message.success('Reply sent successfully!');
                await fetchThreadDetails();
                await fetchContracts();
                resetEditor();
                setSending(false);
                return;
            }
            const thirdPartyForm = new FormData();
            thirdPartyForm.append('agreement_id', pk);
            thirdPartyForm.append('company_id', localStorage.getItem('companyId') || comp_id);
            thirdPartyForm.append('receiver_emails', recipients.join(','));
            thirdPartyForm.append('cc_emails', ccRecipients.join(','));
            thirdPartyForm.append('subject', subject);
            thirdPartyForm.append('body', editorContent);
            thirdPartyForm.append("default_agreement_file", attachDefaultAgreement ? "true" : "false");
            files.forEach((file, idx) => {
                thirdPartyForm.append("attachment", file, file.name || `local_${idx + 1}`);
            });

            const hide = message.loading('Sending email...', 0);
            const res = await axiosInstance.post(`/contract/send-agreement-to-third-party/`, thirdPartyForm);
            hide();
            message.success('Email sent to third party!');
            await fetchThreadDetails();
            await fetchContracts();
            resetEditor();

        } catch (err) {
            if (err?.response?.data?.auth_url) {
                window.open(err?.response?.data?.auth_url, '_blank');
                message.info('Additional authentication required. Please complete the process in the opened window.');
            }
            if (err?.response?.data?.error === "Please complete approvals first: approvals still Draft/Pending/Sent for approval.") {
                message.warning("Please complete approvals first: approvals still Draft/Pending/Sent for approval.");
                return
            }
            message.error(err?.response?.data?.error || 'Something went wrong!');

        } finally {
            setSending(false);
        }
    };

    const handleSendWithAttachments = async () => {
        try {
            if (allRecipientsInOrg && files.length > 0) {
                await handleAttachmentSave();
            }
            await handleSend();
        } catch (err) {
            console.error(err);
        }
    };

    const resetEditor = () => {
        setIsReplyOpen(false);
        setEditorContent('');
        setFiles([]);
        setRecipients([]);
        setCcRecipients([]);
        setSubject('');
        setStatus('');
        setReplyingTo(null);
    };


    const authHandler = async () => {
        try {
            const res = await axiosInstance.get(`email/start-gmail-auth/`);
            if (res.data?.auth_url) {
                window.open(res.data?.auth_url, '_blank');
                message.info('Please complete the authentication in the opened window.');
            }
        } catch (error) {
            console.error("Error initiating Google authentication:", error);
            message.error("Failed to initiate Google authentication. Please try again.");
        }
    }

    useEffect(() => {
        if (isReplyOpen) {
            setTimeout(() => {
                const quillContainer = document.querySelector('.ql-editor');
                if (quillContainer) quillContainer.scrollTop = quillContainer.scrollHeight;
            }, 0);
        }
        if (isReplyOpen && !orgEmails.length) {
            loadOrgEmails();
        }
    }, [isReplyOpen]);

    return (
        <div className="">
            <div className="bg-white dark:bg-[#17153B] rounded shadow-sm">
                <div className="px-6">
                    {messages.length === 0 ? (
                        <div className="mt-2 flex justify-between items-center">
                            <ButtonComp
                                text="Compose Email"
                                bgColor="#4E36A3"
                                textColor="white"
                                onClick={openCompose}
                            />
                            {/* {!google_auth &&
                                <div onClick={authHandler}>

                                    <GoogleButton />
                                </div>

                            } */}
                        </div>
                    ) : (
                        <>
                            <div className="mt-2 flex justify-between items-center">
                                <ButtonComp
                                    text="Compose Email"
                                    bgColor="#4E36A3"
                                    textColor="white"
                                    onClick={openCompose}
                                />
                                {/* {!google_auth &&
                                    <div onClick={authHandler}>
                                        <GoogleButton />
                                    </div>
                                } */}
                            </div>
                            <div className="py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <div className="text-[15px] font-semibold text-gray-800 dark:text-gray-200"><span className='text-gray-700 dark:text-gray-300'>Subject : </span>{messages[0]?.subject}</div>
                                    </div>
                                </div>
                                <div className="text-[14px] text-gray-500">Last updated: {messages[messages.length - 1]?.timestamp ? new Date(messages[messages.length - 1]?.timestamp).toLocaleString() : ''}</div>
                            </div>
                            {messages.map((msg) => {
                                // const { main, history } = splitMessageAndHistory(msg.body);
                                const cleanBody = getCleanEmailBody(msg.body);
                                return (
                                    <EmailThreadItems
                                        key={msg.id}
                                        item={{
                                            ...msg,
                                            body: cleanBody,
                                        }}
                                        id={contract.id}
                                        contract={contract}
                                        fetchContracts={fetchContracts}
                                        onReply={handleReplyClick}
                                    />
                                );
                            })}
                        </>
                    )}
                </div>
            </div>
            {isReplyOpen && (
                <div
                    className={
                        isFullScreen
                            ? "fixed inset-0 w-[90vw] h-[90vh] mx-auto my-auto rounded-lg bg-white dark:bg-[#122253] border shadow-2xl z-[500000] flex flex-col"
                            : replyingTo
                                ? "fixed right-0 bottom-[-15px] w-[1000px] bg-white dark:bg-[#122253] border-t rounded-t-lg shadow-2xl z-[500000] flex flex-col"
                                : "fixed bottom-[-20px] right-4 w-[600px] bg-white dark:bg-[#122253] border rounded-lg shadow-2xl z-[500000] flex flex-col"
                    }
                    role="dialog"
                    aria-label={replyingTo ? 'Reply Editor' : 'Compose Email Editor'}
                >
                    <div className="p-1 border-b flex justify-between items-center bg-gray-100 dark:bg-[#122253]">
                        <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                            {replyingTo
                                ? `${replyAllMode ? 'Reply all' : 'Reply'} to: ${replyingTo.sender_email}`
                                : 'Compose New Email'}
                        </span>

                        <div className="flex items-center gap-4">
                            {!allRecipientsInOrg &&

                                <label className="flex items-center gap-2 text-[14px] text-blue-800 font-bold dark:text-gray-200">
                                    <Checkbox
                                        checked={attachDefaultAgreement}
                                        onChange={(e) => setAttachDefaultAgreement(e.target.checked)}
                                    />
                                    Attach agreement
                                </label>

                            }
                            <SelectTemplateModal contract={contract} editable_user={selfName}
                                onSelectTemplate={({ subject, body }) => {
                                    setSubject(subject);
                                    setEditorContent(body);
                                }} />
                            <ColapseButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />


                            <button
                                onClick={() => setIsReplyOpen(false)}
                                className="text-red-500 text-xs font-medium hover:underline"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                    <div className="p-1 flex flex-col gap-1 h-full">
                        {/* Recipients */}
                        <CreatableSelect
                            isMulti
                            options={
                                contract?.status === "EXECUTED"
                                    ? []
                                    : selfName === contract?.editable_username
                                        ? (contract?.contract_access_users || [])
                                            .filter(u => u.email && u.email !== selfEmail)
                                            .map(u => ({
                                                label: `${u.email} (${u.first_name || ''}) - ${u.role || ''}`,
                                                value: u.email,
                                                userId: u.id,
                                            }))
                                        : []
                            }
                            value={recipients.map(r => {
                                const user = (contract?.contract_access_users || []).find(u => u.email === r);
                                return { label: r, value: r, userId: user?.id };
                            })}
                            onChange={(vals) => {
                                console.log(vals, "vvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvv");
                                setRecipients(vals.map(v => v.value));
                                setSelectedUserIds(vals.map(v => v.userId).filter(Boolean));
                            }}
                            placeholder="Enter recipients"
                            styles={reactSelectTheme}
                            className="min-h-[40px]"
                        />

                        {allRecipientsInOrg && selfName === contract?.editable_username ? (
                            <div className="mt-2">
                                <Select
                                    options={agreementStatus
                                        .filter(s => {
                                            if (
                                                (userRole.toLowerCase() === "businesshead" || userRole.toLowerCase() === "admin" || userRole.toLowerCase() === "businessteam") &&
                                                s.value === "EXECUTED"
                                            ) return false;
                                            return true;
                                        })
                                        .map(s => ({ label: s.label, value: s.value }))}
                                    value={status ? agreementStatus.find(s => s.value === status) : null}
                                    onChange={(selectedOption) => setStatus(selectedOption.value)}
                                    placeholder="Select Status"
                                    styles={reactSelectTheme}
                                    className="min-h-[40px]"
                                />
                                <div className="text-[11px] text-gray-500 mt-1">
                                    All recipients are within your organisation; status is required.
                                </div>
                            </div>
                        ) : (
                            <>
                                <CreatableSelect
                                    isMulti
                                    options={[]}
                                    value={ccRecipients.map(r => ({ label: r, value: r }))}
                                    onChange={(vals) => setCcRecipients(vals.map(v => v.value))}
                                    placeholder="Enter CC recipients"
                                    styles={reactSelectTheme}
                                    className="min-h-[40px] "
                                />

                                {!replyingTo && (
                                    <Input
                                        placeholder="Subject"
                                        variant="underlined"
                                        className="p-2 dark:bg-[#122253] rounded text-sm h-8"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                    />
                                )}
                            </>
                        )}

                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={editorContent}
                            onChange={setEditorContent}
                            modules={modules}
                            className={`${isFullScreen ? "h-[300px]" : replyingTo ? "h-[150px]" : "h-[200px]"} dark:[&_.ql-editor]:text-white`}
                        />

                        {replyingTo && quotedHistory && (
                            <div className="mt-2">
                                {!showQuotedHistory ? (
                                    <div className="flex justify-start">
                                        <button
                                            className="text-gray-400 mt-4 hover:text-gray-600 text-xl font-bold px-2 rounded"
                                            onClick={() => setShowQuotedHistory(true)}
                                            title="Show quoted text"
                                        >
                                            ...
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative mt-2 border-l-2 pl-3 text-sm max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-[#0E0E2C] rounded">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-gray-500 text-xs">Quoted History</span>
                                            <button
                                                className="text-gray-400 hover:text-gray-600 text-sm font-semibold"
                                                onClick={() => setShowQuotedHistory(false)}
                                            >
                                                Hide
                                            </button>
                                        </div>
                                        <ReactQuill
                                            theme="snow"
                                            value={quotedHistory}
                                            onChange={setQuotedHistory}
                                            modules={modules}
                                            className={`${isFullScreen ? "h-[140px]" : replyingTo ? "h-[100px]" : "h-[202px]"} dark:[&_.ql-editor]:text-white`}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className={`sticky left-0 right-0  px-3 py-1 border-t flex justify-end gap-2 bg-gray-50 dark:bg-[#122253] ${replyingTo ? "" : "mt-[38px]"}`}>
                        <label className={`border h-10 pt-2 border-dashed rounded px-3 text-center text-[14px] text-gray-500 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#17153B] transition-colors `}>
                            Drop files here or <span className="text-blue-600">Browse</span> (Up to 20 MB)
                            <input type="file" className="hidden" multiple onChange={handleFileChange} />
                        </label>
                        {files.length > 0 && (
                            <ul className="text-[10px] text-gray-600 dark:text-gray-300 h-10 overflow-y-auto mx-3">
                                {files.map((file, idx) => (
                                    <li key={idx}>{file.name}</li>
                                ))}
                            </ul>
                        )}
                        <button onClick={() => setIsReplyOpen(false)} className="px-3 py-1 h-10 text-sm border rounded hover:bg-gray-100 dark:hover:bg-[#17153B]">  Cancel  </button>
                        <button
                            onClick={handleSendWithAttachments}
                            disabled={sending}
                            className={`px-3 py-1 h-10 text-sm rounded flex items-center gap-1 
                                ${sending ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                        >
                            {sending ? 'Sending...' : <>Send <SendOutlined /></>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// import React, { useEffect, useState } from 'react';
// import { useSelector } from 'react-redux';
// import axiosInstance from '../../../utils/axiosInstance';
// import EmailThreadHeader from '../../../components/email/EmailThreadHeader';
// import EmailThreadList from '../../../components/email/EmailThreadList';
// import EmailComposeModal from '../../../components/email/EmailComposeModal';
// import { useTheme } from '../../../utils/themeToggle/theme';

// export default function EmailThread({ contract, fetchContracts }) {
//     const [isReplyOpen, setIsReplyOpen] = useState(false);
//     const [messages, setMessages] = useState([]);
//     const [recipients, setRecipients] = useState([]);
//     const [ccRecipients, setCcRecipients] = useState([]);
//     const [editorContent, setEditorContent] = useState('');
//     const [files, setFiles] = useState([]);
//     const [status, setStatus] = useState('');
//     const comp_id = useSelector(store => store.auth.userInfo.company_id);
//     const selfEmail = useSelector(store => store.auth.userInfo.user_email);
//     const selfName = useSelector(store => store.auth.userInfo.name);
//     const userRole = useSelector(store => store.auth.userInfo.role);
//     const google_auth = useSelector(store => store.auth.userInfo.is_google_authenticated);
//     const { reactSelectTheme } = useTheme();

//     const fetchThreadDetails = async () => {
//         try {
//             const res = await axiosInstance.get(`/agreements/${contract.id}/conversation/`);
//             setMessages(res.data.messages || []);
//         } catch (err) {
//             console.error(err);
//         }
//     };

//     useEffect(() => fetchThreadDetails(), []);

//     return (
//         <div className="bg-white dark:bg-[#17153B] rounded shadow-sm px-6 py-4">
//             <EmailThreadHeader
//                 messages={messages}
//                 openCompose={() => setIsReplyOpen(true)}
//                 google_auth={google_auth}
//                 authHandler={() => window.open('auth_url', '_blank')}
//             />

//             <EmailThreadList
//                 messages={messages}
//                 contract={contract}
//                 fetchContracts={fetchContracts}
//                 handleReplyClick={() => setIsReplyOpen(true)}
//             />

//             {isReplyOpen && (
//                 <EmailComposeModal
//                     isReplyOpen={isReplyOpen}
//                     replyingTo={null}
//                     showQuotedHistory={false}
//                     setShowQuotedHistory={() => {}}
//                     recipients={recipients}
//                     setRecipients={setRecipients}
//                     ccRecipients={ccRecipients}
//                     setCcRecipients={setCcRecipients}
//                     selectedUserIds={[]}
//                     setSelectedUserIds={() => {}}
//                     editorContent={editorContent}
//                     setEditorContent={setEditorContent}
//                     status={status}
//                     setStatus={setStatus}
//                     files={files}
//                     handleFileChange={(e) => setFiles([...e.target.files])}
//                     handleSendWithAttachments={() => {}}
//                     attachDefaultAgreement={true}
//                     setAttachDefaultAgreement={() => {}}
//                     isFullScreen={false}
//                     setIsFullScreen={() => {}}
//                     contract={contract}
//                     selfName={selfName}
//                     allRecipientsInOrg={true}
//                     reactSelectTheme={reactSelectTheme}
//                 />
//             )}
//         </div>
//     );
// }
